import { useMemo, useCallback } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { Cell } from "../RowCell/Cells/Cell";
import { FormattedTableRowData } from "../types";
import { ReportHeaderRenderer } from "../ReportHeaderRenderer";
import { useAddStepColumn } from "../../Blueprints/EditBlueprint/hooks/useAddStepColumn";
import { useReportContext } from "../../report-context";

import { TableColumn } from "@/components/ui/TableV2";
import { formatInputVariable } from "@/utils/evaluate";
import { ReportColumn, ColumnType } from "@/types/evaluate";
import { cn } from "@/lib/utils";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries";

const getColumnMinWidth = (column: ReportColumn): number => {
  if (column.id === -999 || column.column_type === ColumnType.DATASET) {
    return 250;
  }
  return 180;
};

export const useTableColumns = (gridRef: React.RefObject<AgGridReact>) => {
  const reportContext = useReportContext();
  const { sortedColumns, reportId, isEditable } = reportContext;
  const { isEditDisabled } = useRbac();
  const cannotEditBlueprint = isEditDisabled(EntityType.REPORT);

  const cellRenderer = useCallback((params: ICellRendererParams) => {
    const cellData = params.value;
    return <Cell cell={cellData.cell} column={cellData.column} />;
  }, []);

  const columnStructureKey = useMemo(
    () =>
      sortedColumns
        .map((col: ReportColumn) => `${col.id}-${col.column_type}-${col.position}`)
        .join("|"),
    [sortedColumns],
  );

  const tableColumns: TableColumn<FormattedTableRowData>[] = useMemo(
    () =>
      sortedColumns.map((column: ReportColumn) => ({
        header: formatInputVariable(column.column_type, column.name),
        field: String(column.id),
        name: column.name,
        lockPosition:
          column.column_type === ColumnType.DATASET || !isEditable || cannotEditBlueprint
            ? "left"
            : false,
        cellRenderer,
        columnType: column.column_type,
        headerComponent: ReportHeaderRenderer,
        suppressMovable: column.column_type === ColumnType.DATASET,
        minWidth: getColumnMinWidth(column),
        id: Number(column.id || 0),
        headerClass: cn("!p-0 border-none", column.id === -999 && "bg-amber-100"),
        cellClass: cn("!p-0 border-none", column.id === -999 && "bg-amber-100"),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnStructureKey, isEditable, cellRenderer, cannotEditBlueprint],
  );

  const addNewColumn: TableColumn<FormattedTableRowData> = useAddStepColumn({
    reportId,
    sortedBlueprintColumns: sortedColumns,
    gridRef,
  });

  const tableColumnsWithAddNewColumn = useMemo(() => {
    if (isEditable && !cannotEditBlueprint) {
      return [...tableColumns, addNewColumn];
    }
    return tableColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable, tableColumns]);

  return {
    tableColumns,
    tableColumnsWithAddNewColumn,
    cellRenderer,
  };
};

export default useTableColumns;
